import {useEffect, useRef, useState} from 'react';

/**
 * Client-side "streaming": reveal an already-known string progressively so an
 * AI answer appears to write itself out (the backend is synchronous — this is
 * a display animation only).
 *
 *   active === false → returns the full text immediately (historical/loaded
 *                      messages, user bubbles, errors render instantly).
 *   active === true  → reveals 0 → full at a consistent character rate.
 *
 * Re-reveals when `fullText` changes (regenerate / edit) and never restarts on
 * unrelated re-renders (progress is held in refs).
 */

const REVEAL_CHARS_PER_SECOND = 120;
const REVEAL_TICK_MS = 40; // ~25 visual updates/sec → fewer markdown re-parses
const CHARS_PER_MS = REVEAL_CHARS_PER_SECOND / 1000;

const prefersReducedMotion = () =>
    typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const clampRevealLength = (fullText: string, elapsedMs: number) =>
    Math.min(fullText.length, Math.floor(elapsedMs * CHARS_PER_MS));

const safeSliceEnd = (text: string, index: number): number => {
    if (index <= 0 || index >= text.length) return index;

    const previousCodeUnit = text.charCodeAt(index - 1);
    const nextCodeUnit = text.charCodeAt(index);
    const splitSurrogatePair =
        previousCodeUnit >= 0xd800
        && previousCodeUnit <= 0xdbff
        && nextCodeUnit >= 0xdc00
        && nextCodeUnit <= 0xdfff;

    return splitSurrogatePair ? index - 1 : index;
};

export const useRevealedText = (fullText: string, active: boolean): string => {
    const [shown, setShown] = useState(active ? '' : fullText);
    const rafRef = useRef<number | null>(null);
    const startedAtRef = useRef<number | null>(null);
    const lastPaintAtRef = useRef(0);
    const revealedLengthRef = useRef(0);
    const animatedTextRef = useRef<string | null>(null);

    useEffect(() => {
        const cancel = () => {
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        };

        // not animating → show the full text
        if (!active || !fullText || prefersReducedMotion()) {
            cancel();
            revealedLengthRef.current = fullText.length;
            animatedTextRef.current = fullText;
            setShown(fullText);
            return cancel;
        }

        // this exact text was already fully revealed → don't restart on re-render
        if (animatedTextRef.current === fullText && revealedLengthRef.current >= fullText.length) {
            setShown(fullText);
            return cancel;
        }

        // (re)start for new / changed text
        animatedTextRef.current = fullText;
        startedAtRef.current = null;
        lastPaintAtRef.current = 0;
        revealedLengthRef.current = 0;
        setShown('');

        const step = (now: number) => {
            if (startedAtRef.current === null) startedAtRef.current = now;

            const elapsedMs = now - startedAtRef.current;
            const target = clampRevealLength(fullText, elapsedMs);

            if (target >= fullText.length) {
                revealedLengthRef.current = fullText.length;
                setShown(fullText);
                rafRef.current = null;
                return;
            }

            if (now - lastPaintAtRef.current >= REVEAL_TICK_MS) {
                lastPaintAtRef.current = now;
                const nextLength = safeSliceEnd(fullText, target);
                if (nextLength > revealedLengthRef.current) {
                    revealedLengthRef.current = nextLength;
                    setShown(fullText.slice(0, nextLength));
                }
            }
            rafRef.current = requestAnimationFrame(step);
        };

        rafRef.current = requestAnimationFrame(step);
        return cancel;
    }, [fullText, active]);

    return shown;
};
